import React, { useState, useEffect, useCallback } from "react";

const API = "https://api.pharmacegroup.co.uk";

/**
 * Admin > Invoice processing.
 *
 * A developer's view of the extraction pipeline: what arrived, what it was
 * turned into, which rule produced each field, and what was finally written.
 *
 * The layout follows the pipeline itself — documents on the left, then the
 * stages left to right — because the question being asked is almost always
 * "where did this go wrong", and that is a question about position in a chain.
 *
 * Rules are edited here rather than in a .js file, and every suggestion is
 * tested against the other stored documents before it can be enabled. A rule
 * that fixes one supplier and quietly breaks six is the failure this screen
 * exists to prevent.
 */
const InvoiceProcessing = () => {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rules, setRules] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("pipeline");   // pipeline | rules | ocr | inbox
  const [inbox, setInbox] = useState(null);
  const [draft, setDraft] = useState(null);     // a suggested or edited rule
  const [testResult, setTestResult] = useState(null);
  const [showText, setShowText] = useState(false);
  const [batch, setBatch] = useState(null);
  const [setup, setSetup] = useState(null);      // { suppliers, parserKeys, ... }
  const [addrs, setAddrs] = useState(null);
  const [editSup, setEditSup] = useState(null);
  const [accts, setAccts] = useState(null);
  const [editAcct, setEditAcct] = useState(null);
  const [assignTo, setAssignTo] = useState("");        // branch
  const [onlyUnplaced, setOnlyUnplaced] = useState(false);
  const [assignSup, setAssignSup] = useState(null);    // supplier correction
  const [assignAcct, setAssignAcct] = useState(null);  // account correction
  const [pages, setPages] = useState(null);           // photographed pages
  const [lightbox, setLightbox] = useState(null);
  const [ocr, setOcr] = useState(null);               // the extraction queue
  const [showRaw, setShowRaw] = useState(false);

  const token = localStorage.getItem("authToken");

  const post = useCallback(async (path, body = {}) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
    return j;
  }, [token]);

  const loadQueue = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await post("/api/invoiceAdmin/queue", { limit: 100 });
      setDocs(d.documents || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [post]);

  const loadRules = useCallback(async () => {
    try {
      const d = await post("/api/invoiceAdmin/rules");
      setRules(d.rules || []); setFields(d.fields || []);
    } catch (e) { setError(e.message); }
  }, [post]);

  useEffect(() => { loadQueue(); loadRules(); }, [loadQueue, loadRules]);

  const openDoc = async (d) => {
    setSelected(d._id); setDetail(null); setShowText(false);
    setPages(null); setShowRaw(false);
    try {
      const det = await post("/api/invoiceAdmin/document", { documentId: d._id });
      setDetail(det);

      // Fetched behind the detail, never with it: a dozen A4 pages would make
      // the extraction table - the thing this screen exists for - wait on
      // several megabytes of image.
      if (det.photo) {
        post("/api/invoiceAdmin/documentPages", { documentId: d._id })
          .then((r) => setPages(r.pages || []))
          .catch(() => setPages([]));
      }
      // the branch dropdown lives in the accounts payload; fetch it once so
      // the assign control is usable without visiting the Setup tab first
      if (!accts) {
        post("/api/invoiceAdmin/accounts").then(setAccts).catch(() => {});
      }
    } catch (e) { setError(e.message); }
  };

  const act = async (label, fn) => {
    setBusy(label); setError(null);
    try { await fn(); } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  /* ------------------------------------------------------------- styling */
  const S = {
    card: { border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff",
            marginBottom: 12 },
    head: { padding: "7px 10px", background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0", fontWeight: "bold", fontSize: 13 },
    btn: (bg) => ({ background: bg || "#fff", color: bg ? "#fff" : "#0f172a",
                    border: "1px solid #cbd5e1", borderRadius: 4,
                    padding: "5px 10px", cursor: "pointer", fontSize: 13,
                    marginRight: 6 }),
    mono: { fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 11.5 },
    th: { textAlign: "left", padding: "5px 7px", fontSize: 11.5, color: "#475569" },
    td: { padding: "5px 7px", fontSize: 12 },
  };

  const statusChip = (s) => {
    const map = {
      parsed: ["#dcfce7", "#15803d"], needsReview: ["#fff4e5", "#a05a00"],
      failed: ["#fee2e2", "#b91c1c"], new: ["#eff6ff", "#1d4ed8"],
      duplicate: ["#f1f5f9", "#64748b"],
    };
    const [bg, fg] = map[s] || ["#f1f5f9", "#475569"];
    return <span style={{ background: bg, color: fg, borderRadius: 3,
                          padding: "0 6px", fontSize: 11 }}>{s || "?"}</span>;
  };

  const ex = detail?.stages?.extraction;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>Invoice processing</h2>
        <div>
          <button style={S.btn("#1d4ed8")} disabled={busy === "run"}
            onClick={() => act("run", async () => {
              const r = await post("/api/invoiceAdmin/runNow");
              await loadQueue();
              alert(r.collected != null
                ? `Collected ${r.collected}, processed ${r.processed}, skipped ${r.skipped || 0} already seen.`
                : JSON.stringify(r));
            })}>
            {busy === "run" ? "Running…" : "Run now"}
          </button>
          {/* Separate from Run now on purpose: one collects what is new, the
              other re-runs mail already processed. Merging them would make an
              ordinary collection destructive. */}
          <button style={S.btn()} disabled={busy === "reproc"}
            title="Re-run messages already processed through the current rules, replacing the previous result."
            onClick={() => act("reproc", async () => {
              if (!window.confirm(
                "Re-process mail that has already been collected?\n\n" +
                "Previous results are replaced. Invoices with stock booked in " +
                "against them are left alone."
              )) return;
              const r = await post("/api/invoiceAdmin/runNow", { force: true });
              await loadQueue();
              alert(
                `${r.examined} examined, ${r.reprocessed || 0} reprocessed, ` +
                `${r.processed} ingested, ${r.skipped} left alone.`
              );
            })}>
            {busy === "reproc" ? "Re-processing…" : "Re-process mail"}
          </button>
          <button style={S.btn()} disabled={busy === "inbox"}
            onClick={() => act("inbox", async () => {
              setInbox(await post("/api/invoiceAdmin/inbox", { limit: 50 }));
              setTab("inbox");
            })}>
            View inbox
          </button>
          {/* Fixing a rule only helps if the documents that failed under the
              old one get re-read. Dry run first: it changes nothing and shows
              exactly which documents would move. */}
          <button style={S.btn()} disabled={busy === "dry"}
            onClick={() => act("dry", async () => {
              setBatch(await post("/api/invoiceAdmin/reprocessAll", { dryRun: true }));
            })}>
            {busy === "dry" ? "Checking…" : "Reprocess failed (dry run)"}
          </button>
          <button style={S.btn()} onClick={loadQueue}>Refresh</button>
          {/* Deliberately last, plain, and two-step. During setup "everything
              here is test wreckage" is usually true, and picking through it by
              origin is slower than resending a few emails. */}
          <button style={{ ...S.btn(), borderColor: "#e0b4b4", color: "#b91c1c" }}
            disabled={busy === "reset"}
            onClick={() => act("reset", async () => {
              const dry = await post("/api/invoiceAdmin/resetAll",
                { confirm: "DELETE ALL INVOICES", dryRun: true });
              const ok = window.confirm(
                `Delete ${dry.wouldDelete.invoices} invoice(s) and ` +
                `${dry.wouldDelete.documents} document(s)?\n\n` +
                // Said out loud, because the button reads as "wipe everything"
                // and somebody resetting to re-run an extraction needs to know
                // the photographs are still going to be there afterwards.
                `${dry.wouldKeep
                    ? `${dry.wouldKeep.pages} photographed page(s) will be KEPT.\n\n`
                    : ""}` +
                `${dry.keptWithScans} with stock booked in will be kept.\n` +
                `Account mappings are kept.\n\n` +
                `Mailbox messages are untouched — running collection again ` +
                `will bring them back in.`
              );
              if (!ok) return;

              // Offered only when it matters, and as a separate decision:
              // "delete everything" and "delete things stock was booked
              // against" are not the same question.
              let includeScanned = false;
              if (dry.keptWithScans > 0) {
                includeScanned = window.confirm(
                  `${dry.keptWithScans} invoice(s) have stock booked in against ` +
                  `them and would normally be kept.\n\n` +
                  `OK to delete those as well, Cancel to keep them.`
                );
              }

              const r = await post("/api/invoiceAdmin/resetAll",
                { confirm: "DELETE ALL INVOICES", includeScanned });
              await loadQueue();
              setDetail(null); setSelected(null);
              alert(`Removed ${r.invoicesRemoved} invoice(s) and ` +
                    `${r.documentsRemoved} document(s).`);
            })}>
            Reset all invoice data
          </button>
        </div>
      </div>

      <p style={{ fontSize: "0.9em", color: "#555", marginTop: 6, maxWidth: 940 }}>
        Every document keeps each stage it passed through, so a bad result can be
        traced to the stage that produced it rather than reproduced by guesswork.
        Extraction below is re-run against the <em>current</em> rules, so editing
        a rule shows its effect immediately without reprocessing anything.
      </p>

      <div style={{ display: "flex", gap: 4, margin: "10px 0",
                    borderBottom: "2px solid #e2e8f0" }}>
        {[["pipeline", "Pipeline"], ["rules", `Rules (${rules.length})`],
          ["ocr", "Photo queue"], ["inbox", "Inbox"], ["setup", "Setup"]]
          .map(([k, label]) => (
          <button key={k} onClick={async () => {
              setTab(k);
              if (k === "ocr") {
                try { setOcr(await post("/api/invoiceAdmin/ocrQueue", { limit: 100 })); }
                catch (e) { setError(e.message); }
              }
              if (k === "setup" && !setup) {
                try {
                  setSetup(await post("/api/invoiceAdmin/suppliers"));
                  setAddrs(await post("/api/invoiceAdmin/branchAddresses"));
                  setAccts(await post("/api/invoiceAdmin/accounts"));
                } catch (e) { setError(e.message); }
              }
            }}
            style={{ ...S.btn(), border: 0,
                     borderBottom: tab === k ? "3px solid #1d4ed8" : "3px solid transparent",
                     fontWeight: tab === k ? "bold" : "normal",
                     color: tab === k ? "#1d4ed8" : "#334155" }}>{label}</button>
        ))}
      </div>

      {error && (
        <div style={{ background: "#fff4f4", border: "1px solid #e0b4b4",
                      padding: "8px 10px", marginBottom: 10 }}>{error}</div>
      )}

      {batch && (
        <div style={{ ...S.card, borderColor: "#bfdbfe", background: "#f8fbff" }}>
          <div style={{ ...S.head, background: "#eff6ff" }}>
            {batch.dryRun ? "Dry run — nothing has changed yet" : "Reprocessed"}
          </div>
          <div style={{ padding: 10, fontSize: 12.5 }}>
            Examined {batch.examined} ·{" "}
            <b style={{ color: "#15803d" }}>{batch.fixed} would now parse</b> ·{" "}
            <span style={{ color: "#a05a00" }}>{batch.stillFailing} still failing</span> ·{" "}
            {batch.unchanged} unchanged
            <div style={{ maxHeight: 160, overflowY: "auto", marginTop: 6 }}>
              {batch.changes?.map((c, i) => (
                <div key={i} style={{ ...S.mono, padding: "1px 0" }}>
                  <span style={{ color: c.now === "parsed" ? "#15803d" : "#b91c1c" }}>
                    {c.was} → {c.now}
                  </span>{" "}
                  <span style={{ color: "#64748b" }}>{c.label}</span>
                  {c.problems?.length ? ` · ${c.problems[0]}` : ""}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8 }}>
              {batch.dryRun && batch.fixed > 0 && (
                <button style={S.btn("#16a34a")} disabled={busy === "apply"}
                  onClick={() => act("apply", async () => {
                    setBatch(await post("/api/invoiceAdmin/reprocessAll", {}));
                    await loadQueue();
                  })}>
                  Apply to {batch.fixed} document{batch.fixed === 1 ? "" : "s"}
                </button>
              )}
              <button style={S.btn()} onClick={() => setBatch(null)}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================== PIPELINE ==== */}
      {tab === "pipeline" && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>

          {/* 1. documents */}
          <div style={{ flex: "0 0 250px", ...S.card, maxHeight: "72vh", overflowY: "auto" }}>
            <div style={S.head}>
              Incoming ({docs.length})
              {docs.filter((d) => !d.pharmacyId).length > 0 && (
                <div style={{ fontWeight: "normal", fontSize: 11.5, marginTop: 3 }}>
                  <label style={{ color: "#a05a00", cursor: "pointer" }}>
                    <input type="checkbox" checked={onlyUnplaced}
                      onChange={(e) => setOnlyUnplaced(e.target.checked)} />
                    {" "}show only the {docs.filter((d) => !d.pharmacyId).length}{" "}
                    without a branch
                  </label>
                </div>
              )}
            </div>
            {loading && <div style={{ padding: 10 }}>Loading…</div>}
            {!loading && docs.length === 0 && (
              <div style={{ padding: 10, color: "#64748b", fontSize: 12.5 }}>
                Nothing collected yet. Push a test document in with
                <code> /invoiceIngestTest</code>, or press Run now once mail
                collection is wired up.
              </div>
            )}
            {docs.filter((d) => !onlyUnplaced || !d.pharmacyId).map((d) => (
              <div key={d._id} onClick={() => openDoc(d)}
                style={{ padding: "7px 9px", borderBottom: "1px solid #f1f5f9",
                         cursor: "pointer",
                         background: selected === d._id ? "#e8f4ef" : "transparent",
                         borderLeft: selected === d._id ? "4px solid #177245"
                                                        : "4px solid transparent" }}>
                <div style={{ fontWeight: "bold", fontSize: 12.5 }}>
                  {d.supplierName || d.supplierKey || "unknown supplier"}
                </div>
                <div style={{ fontSize: 11.5, color: "#475569" }}>
                  {d.invoiceNumber || d.filename || "no number"}
                </div>
                <div style={{ marginTop: 3 }}>
                  {statusChip(d.status)}
                  {/* An invoice nobody could place is a different problem from
                      one that failed to parse: the parsing is fine, it simply
                      has nowhere to go. Flagged separately so it is not lost
                      among genuine parse failures. */}
                  {!d.pharmacyId && (
                    <span style={{ background: "#fff4e5", color: "#a05a00",
                                   borderRadius: 3, padding: "0 6px", fontSize: 11,
                                   marginLeft: 4 }}>
                      no branch
                    </span>
                  )}
                  {/* A photographed document has no text stage and no rules.
                      Marked here so nobody opens one, finds stage 2 empty and
                      goes looking for a bug in the extractor. */}
                  {d.photo?.pageCount > 0 && (
                    <span style={{ background: "#eef4ff", color: "#1c4587",
                                   borderRadius: 3, padding: "0 6px", fontSize: 11,
                                   marginLeft: 4 }}>
                      photo · {d.photo.pageCount}p
                    </span>
                  )}
                  <span style={{ marginLeft: 5, fontSize: 11, color: "#64748b" }}>
                    {d.rowCount ?? 0} rows
                    {d.unparsedCount > 0 && ` · ${d.unparsedCount} unparsed`}
                    {d.reconciled === false && " · ✗ totals"}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* 2-5. the stages */}
          <div style={{ flex: 1, maxHeight: "72vh", overflowY: "auto" }}>
            {!detail && (
              <div style={{ ...S.card, padding: 16, color: "#64748b" }}>
                Select a document to follow it through the pipeline.
              </div>
            )}

            {detail && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <button style={S.btn()} disabled={busy === "re"}
                    onClick={() => act("re", async () => {
                      await post("/api/invoiceAdmin/reprocess", { documentId: selected });
                      await openDoc({ _id: selected }); await loadQueue();
                    })}>
                    {busy === "re" ? "Reprocessing…" : "Reprocess with current rules"}
                  </button>
                </div>

                {/* --- placing an invoice nobody could match --- */}
                {!detail.document.pharmacyId && (
                  <div id="assign-account"
                       style={{ ...S.card, borderColor: "#f0dcae", background: "#fffaf0" }}>
                    <div style={{ ...S.head, background: "#fdf3e0" }}>
                      Associate this account number with a pharmacy
                    </div>
                    <div style={{ padding: 10, fontSize: 12.5 }}>
                      <div style={{ marginBottom: 6 }}>
                        This invoice cannot be placed because account{" "}
                        <b style={{ ...S.mono, fontSize: 14 }}>
                          {detail.stages.extraction?.fields?.supplierAccount || "(none found)"}
                        </b>{" "}
                        is not yet linked to a pharmacy.
                        {detail.document.supplierKey && (
                          <> with <b>{detail.document.supplierKey}</b></>
                        )}
                        {detail.stages.raw.to && (
                          <> · sent to <span style={{ ...S.mono, color: "#94a3b8" }}
                               title="Not used to decide the branch — forwarded invoices arrive from whatever mailbox the branch happens to use.">
                            {detail.stages.raw.to}</span></>
                        )}
                      </div>
                      {detail.stages.extraction?.fields?.branchName && (
                        <div style={{ color: "#64748b", marginBottom: 6 }}>
                          The invoice itself says{" "}
                          <b>{detail.stages.extraction.fields.branchName}</b> — a hint
                          only, since the wording on the document is not reliable.
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 8, alignItems: "flex-end",
                                    flexWrap: "wrap" }}>
                        {/* Supplier and account are editable here because an
                            invoice that could not be placed usually could not
                            be placed BECAUSE one of them was read wrongly.
                            Fixing only the branch leaves the next one stuck. */}
                        <label style={{ fontSize: 12.5 }}>
                          Supplier
                          <input
                            value={assignSup ?? (detail.document.supplierName
                              || detail.document.supplierKey || "")}
                            placeholder="not identified"
                            style={{ display: "block", padding: 5, width: 180 }}
                            onChange={(e) => setAssignSup(e.target.value)} />
                        </label>
                        <label style={{ fontSize: 12.5 }}>
                          Account number
                          <input
                            value={assignAcct ?? (detail.document.supplierAccount
                              || detail.stages.extraction?.fields?.supplierAccount || "")}
                            placeholder="none on the invoice"
                            style={{ ...S.mono, display: "block", padding: 5, width: 150 }}
                            onChange={(e) => setAssignAcct(e.target.value)} />
                        </label>
                        <label style={{ fontSize: 12.5 }}>
                          Which branch is this?
                          <select value={assignTo} style={{ display: "block", padding: 5,
                                                            minWidth: 280 }}
                            onChange={(e) => setAssignTo(e.target.value)}>
                            <option value="">Choose a branch…</option>
                            {(accts?.branches || []).map((b) => (
                              <option key={b.pharmacyId} value={b.pharmacyId}>
                                {b.organisation} — {b.pharmacyName}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button style={S.btn("#16a34a")}
                          disabled={!assignTo || busy === "assign"}
                          onClick={() => act("assign", async () => {
                            const r = await post("/api/invoiceAdmin/assignDocumentBranch", {
                              documentId: selected, pharmacyId: assignTo,
                              // sent only when actually changed, so an untouched
                              // field cannot overwrite what was parsed correctly
                              ...(assignSup !== null ? { supplierName: assignSup } : {}),
                              ...(assignAcct !== null ? { accountNumber: assignAcct } : {}),
                            });
                            setAssignTo(""); setAssignSup(null); setAssignAcct(null);
                            await loadQueue();
                            await openDoc({ _id: selected });
                            alert(
                              `Assigned to ${r.branch}.\n` +
                              `${r.documentsUpdated} document(s) and ${r.updated} invoice(s) updated.` +
                              (r.rule ? `\n\nRemembered: ${r.rule}` : "")
                            );
                          })}>
                          {busy === "assign" ? "Assigning…" : "Assign and remember"}
                        </button>
                      </div>
                      <div style={{ color: "#64748b", fontSize: 11.5, marginTop: 6 }}>
                        Every copy of this invoice is placed at once, because
                        what is being saved is the account, not this one
                        document. The branch is decided by the account number alone, so
                        this is the one thing that has to be right. The supplier
                        and account above are saved with it, and the account is
                        recorded against that branch, so
                        this invoice and every future one on the same account go
                        there without being asked again. Any other unplaced
                        invoice on this account is updated too.
                      </div>
                    </div>
                  </div>
                )}

                {detail.document.pharmacyId && (
                  <div style={{ ...S.card, background: "#f0fdf4",
                                borderColor: "#bbf7d0" }}>
                    <div style={{ padding: 10, fontSize: 12.5, color: "#15803d" }}>
                      Placed at <b>{detail.document.pharmacyName
                        || detail.document.pharmacyId}</b>
                      {detail.document.resolvedBy?.branch && (
                        <span style={{ color: "#64748b" }}>
                          {" "}· matched by {detail.document.resolvedBy.branch}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {detail.document.status === "duplicate" && (
                  <div style={{ ...S.card, background: "#f8fafc" }}>
                    <div style={{ padding: 10, fontSize: 12.5, color: "#475569" }}>
                      This is the same invoice arriving again &mdash; recognised
                      and not stored a second time. Assigning the account from
                      here is fine: it applies to every copy.
                    </div>
                  </div>
                )}

                {/* stage 1 — photographed */}
                {detail.photo && (
                  <div style={S.card}>
                    <div style={{ ...S.head, display: "flex",
                                  justifyContent: "space-between", alignItems: "center" }}>
                      <span>1 · As photographed
                        {" "}({detail.photo.pageCount} page
                        {detail.photo.pageCount === 1 ? "" : "s"})</span>
                      <span style={{ fontWeight: "normal", fontSize: 11.5,
                                     color: "#64748b" }}>
                        {/* The first question about a bad read is whether the
                            right layout description was even used. Without
                            this, answering it means going to the database.

                            layoutKey, not profileKey. The field was renamed
                            when the second OCR pathway was removed: two
                            different kinds of "profile" used to write their key
                            into one field, so the value could not be
                            interpreted without knowing which pathway produced
                            it. This one only ever means a supplier layout
                            description. */}
                        {detail.photo.layoutKey && (
                          <span style={{
                            background: detail.photo.layoutKey === "generic"
                              ? "#fff4e5" : "#e8f5e9",
                            color: detail.photo.layoutKey === "generic"
                              ? "#a05a00" : "#177245",
                            borderRadius: 3, padding: "1px 6px", marginRight: 6,
                          }}>
                            {detail.photo.layoutKey === "generic"
                              ? "no supplier layout matched"
                              : `${detail.photo.layoutKey} layout`}
                          </span>
                        )}
                        {detail.photo.identifiedAs && (
                          <span style={{ marginRight: 6 }}>
                            read as {detail.photo.identifiedAs}
                          </span>
                        )}
                        {detail.photo.model || "no model recorded"}
                        {detail.document.attempt != null &&
                          ` · attempt ${detail.document.attempt}`}
                      </span>
                    </div>
                    {detail.document.error && (
                      <div style={{ padding: 10, color: "#b91c1c", fontSize: 12.5 }}>
                        {detail.document.error}
                      </div>
                    )}
                    <div style={{ padding: 10, display: "flex", gap: 8,
                                  flexWrap: "wrap" }}>
                      {pages === null ? (
                        <span style={{ color: "#64748b", fontSize: 12.5 }}>
                          Loading pages…
                        </span>
                      ) : pages.length === 0 ? (
                        <span style={{ color: "#64748b", fontSize: 12.5 }}>
                          The pages have been deleted, or the invoice was purged.
                        </span>
                      ) : pages.map((pg) => (
                        <div key={pg._id} style={{ width: 108 }}>
                          <img src={`data:${pg.contentType};base64,${pg.imageBase64}`}
                               alt={`Page ${pg.page}`}
                               onClick={() => setLightbox(pg)}
                               style={{ width: "100%", height: 144, objectFit: "cover",
                                        objectPosition: "top", cursor: "zoom-in",
                                        border: "1px solid #cbd5e1" }} />
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                            p{pg.page}
                            {pg.bytes && ` · ${Math.round(pg.bytes / 1024)}kB`}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* The single most useful thing on this screen for a
                        photographed invoice: an image you can zoom into, beside
                        the numbers that came out of it. */}
                    <div style={{ padding: "0 10px 10px", fontSize: 11.5,
                                  color: "#64748b" }}>
                      Click a page to enlarge it and check the figures against
                      what was extracted below.
                    </div>
                  </div>
                )}

                {/* stage 2 — what the model returned */}
                {detail.photo && (
                  <div style={S.card}>
                    <div style={{ ...S.head, display: "flex",
                                  justifyContent: "space-between", alignItems: "center" }}>
                      <span>2 · What the model returned</span>
                      <button style={{ ...S.btn(), marginRight: 0 }}
                              onClick={() => setShowRaw(!showRaw)}>
                        {showRaw ? "Hide" : "Show"} raw reply
                      </button>
                    </div>
                    <div style={{ padding: 10, fontSize: 12.5 }}>
                      {/* There is no normalised-text stage in this path, and
                          none is invented: a fabricated one would sit exactly
                          where a developer goes looking for evidence. */}
                      <div style={{ color: "#64748b", marginBottom: 8 }}>
                        No text stage — the page went to the model as an image,
                        so there is nothing between the photograph and the JSON.
                      </div>
                      {detail.photo.lineConfidence?.length > 0 && (
                        <div>
                          <b>Per-line confidence:</b>{" "}
                          {detail.photo.lineConfidence.map((c, i) => (
                            <span key={i} style={{
                              display: "inline-block", marginRight: 4,
                              padding: "0 5px", borderRadius: 3, fontSize: 11,
                              background: c === 0 ? "#fee2e2"
                                        : c < 0.5 ? "#fff4e5" : "#e8f5e9",
                              color: c === 0 ? "#b91c1c"
                                   : c < 0.5 ? "#a05a00" : "#177245",
                            }}>{c.toFixed(2)}</span>
                          ))}
                          <div style={{ color: "#64748b", marginTop: 4 }}>
                            A whole document coming back low is a photography
                            problem, not a model one — check the lighting and
                            whether the page filled the frame.
                          </div>
                        </div>
                      )}
                    </div>
                    {showRaw && (
                      <pre style={{ ...S.mono, margin: 0, padding: 10, maxHeight: 320,
                                    overflow: "auto", background: "#fcfcfc",
                                    whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(
                          detail.storedExtraction?.raw ??
                          detail.storedExtraction?.normalised ??
                          detail.storedExtraction, null, 2)}
                      </pre>
                    )}
                  </div>
                )}

                {/* stage 1 */}
                {!detail.photo && (
                <div style={S.card}>
                  <div style={S.head}>1 · As received</div>
                  <div style={{ padding: 10, fontSize: 12.5 }}>
                    <div><b>From:</b> {detail.stages.raw.from || "—"}</div>
                    <div><b>To:</b> {detail.stages.raw.to || "—"}</div>
                    <div><b>Subject:</b> {detail.stages.raw.subject || "—"}</div>
                    <div><b>Attachments:</b>{" "}
                      {detail.stages.raw.attachments?.length
                        ? detail.stages.raw.attachments.join(", ") : "none"}</div>
                    <div style={{ color: "#64748b", marginTop: 4 }}>
                      Message id: <span style={S.mono}>{detail.document.messageId || "—"}</span>
                    </div>
                  </div>
                </div>
                )}

                {/* stage 2 */}
                {!detail.photo && (
                <div style={S.card}>
                  <div style={{ ...S.head, display: "flex",
                                justifyContent: "space-between", alignItems: "center" }}>
                    <span>2 · Normalised text</span>
                    <button style={{ ...S.btn(), marginRight: 0 }}
                      onClick={() => setShowText(!showText)}>
                      {showText ? "Hide" : "Show"} ({detail.stages.normalised.chars} chars,
                      {" "}{detail.stages.normalised.lines} lines)
                    </button>
                  </div>
                  {showText && (
                    <pre style={{ ...S.mono, margin: 0, padding: 10, maxHeight: 300,
                                  overflow: "auto", background: "#fcfcfc",
                                  whiteSpace: "pre-wrap" }}>
                      {detail.stages.normalised.text || "(no text stored)"}
                    </pre>
                  )}
                </div>
                )}

                {/* stage 3 — the important one.
                    Rules only exist on the email path; a photographed document
                    has no rule to show, so the whole panel would be an empty
                    table promising an explanation it cannot give. */}
                {!detail.photo && (
                <div style={S.card}>
                  <div style={S.head}>3 · Extraction, with the rule that produced each value</div>
                  {!ex && <div style={{ padding: 10, color: "#64748b" }}>Not extracted.</div>}
                  {ex?.error && (
                    <div style={{ padding: 10, color: "#b91c1c" }}>{ex.error}</div>
                  )}
                  {ex && !ex.error && (
                    <>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ background: "#fafafa" }}>
                          <th style={S.th}>Field</th><th style={S.th}>Value</th>
                          <th style={S.th}>Rule</th><th style={S.th}>Matched</th>
                          <th style={S.th}></th>
                        </tr></thead>
                        <tbody>
                          {Object.entries(ex.fields).map(([k, v]) => {
                            const p = ex.provenance[k] || {};
                            return (
                              <tr key={k} style={{ borderTop: "1px solid #f1f5f9",
                                                   background: v == null ? "#fff8f8" : "transparent" }}>
                                <td style={S.td}>{k}</td>
                                <td style={{ ...S.td, fontWeight: "bold",
                                             color: v == null ? "#b91c1c" : "#0f172a" }}>
                                  {v == null ? "not found" : String(v)}
                                </td>
                                <td style={{ ...S.td, ...S.mono, color: "#64748b" }}>
                                  {p.rule || "—"}
                                </td>
                                <td style={{ ...S.td, ...S.mono, color: "#94a3b8",
                                             maxWidth: 240, overflow: "hidden",
                                             textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    title={p.matched || ""}>
                                  {p.matched || "—"}
                                </td>
                                <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                                  {k === "supplierAccount" && v && !detail.document.pharmacyId && (
                                    <button style={{ ...S.btn("#a05a00"), padding: "2px 7px",
                                                     fontSize: 11 }}
                                      title="Link this account number to a pharmacy, so this and every future invoice on it is placed automatically."
                                      onClick={() => {
                                        setAssignAcct(String(v));
                                        document.getElementById("assign-account")
                                          ?.scrollIntoView({ behavior: "smooth", block: "center" });
                                      }}>
                                      Associate with pharmacy
                                    </button>
                                  )}
                                  <button style={{ ...S.btn(), padding: "2px 7px", fontSize: 11 }}
                                    disabled={busy === "sug"}
                                    onClick={() => act("sug", async () => {
                                      const r = await post("/api/invoiceAdmin/suggestRule",
                                        { documentId: selected, field: k });
                                      setDraft(r.draft); setTestResult(r.test); setTab("rules");
                                    })}>
                                    Fix
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <div style={{ padding: "8px 10px", borderTop: "1px solid #e2e8f0" }}>
                        <b style={{ fontSize: 12.5 }}>
                          {ex.rows.length} line item{ex.rows.length === 1 ? "" : "s"}
                        </b>
                        {ex.unparsed.length > 0 && (
                          <span style={{ color: "#b91c1c", fontSize: 12.5 }}>
                            {" "}· {ex.unparsed.length} looked like items but did not parse
                          </span>
                        )}
                        <button style={{ ...S.btn(), padding: "2px 7px", fontSize: 11, marginLeft: 8 }}
                          disabled={busy === "sugrow"}
                          onClick={() => act("sugrow", async () => {
                            const r = await post("/api/invoiceAdmin/suggestRule",
                              { documentId: selected, field: "row" });
                            setDraft(r.draft); setTestResult(r.test); setTab("rules");
                          })}>
                          Fix rows
                        </button>

                        {ex.unparsed.length > 0 && (
                          <pre style={{ ...S.mono, background: "#fff8f8", padding: 8,
                                        marginTop: 6, maxHeight: 120, overflow: "auto" }}>
                            {ex.unparsed.join("\n")}
                          </pre>
                        )}

                        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
                          <thead><tr style={{ background: "#fafafa" }}>
                            <th style={S.th}>PIP</th><th style={S.th}>Description</th>
                            <th style={S.th}>Pack</th><th style={S.th}>Qty</th>
                            <th style={S.th}>Unit</th><th style={S.th}>Net</th>
                            <th style={S.th}>Rule</th>
                          </tr></thead>
                          <tbody>
                            {ex.rows.map((r, i) => (
                              <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                                <td style={{ ...S.td, ...S.mono }}>{r.pip || "—"}</td>
                                <td style={S.td}>{r.description}</td>
                                <td style={S.td}>{r.packSize || "—"}</td>
                                <td style={S.td}>{r.quantity}</td>
                                <td style={S.td}>
                                  {r.unitPricePence != null
                                    ? `£${(r.unitPricePence / 100).toFixed(2)}` : "—"}
                                </td>
                                <td style={S.td}>
                                  {r.lineTotalPence != null
                                    ? `£${(r.lineTotalPence / 100).toFixed(2)}` : "—"}
                                </td>
                                <td style={{ ...S.td, ...S.mono, color: "#94a3b8" }}>{r._rule}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                )}

                {/* stage 3, photographed — the lines themselves.
                    Same position in the chain as the rule table above, and the
                    same question: what did this document actually become. */}
                {detail.photo && (() => {
                  const lines = detail.storedExtraction?.normalised?.expectedLines || [];
                  return (
                    <div style={S.card}>
                      <div style={S.head}>
                        3 · Lines extracted ({lines.length})
                      </div>
                      {lines.length === 0 ? (
                        <div style={{ padding: 10, color: "#64748b", fontSize: 12.5 }}>
                          Nothing was extracted from these pages.
                        </div>
                      ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead><tr style={{ background: "#fafafa" }}>
                            <th style={S.th}>Description</th>
                            <th style={S.th} title={
                              "The wholesaler's own PIP code, and the barcode " +
                              "printed against the line. The barcode is what " +
                              "the scanner reads off the pack, so a line with " +
                              "one is identified exactly; the PIP has nothing " +
                              "on the box to check against and is recorded only."
                            }>Codes</th>
                            <th style={{ ...S.th, textAlign: "right" }}>Pack</th>
                            <th style={{ ...S.th, textAlign: "right" }}>Qty</th>
                            <th style={S.th} title={
                              "What each of the three independent readings of " +
                              "the quantity said: the invoice column, the " +
                              "returns/picking panel, and the figure implied " +
                              "by unit price and line total."
                            }>inv / ret / £</th>
                            <th style={{ ...S.th, textAlign: "right" }}>Unit</th>
                            <th style={{ ...S.th, textAlign: "right" }}>Line</th>
                            <th style={{ ...S.th, textAlign: "right" }}>Conf.</th>
                          </tr></thead>
                          <tbody>
                            {lines.map((l, i) => (
                              <tr key={i} style={{
                                borderTop: "1px solid #f1f5f9",
                                background: l.quantity === 0 ? "#fef2f2"
                                          : l.confidence < 0.5 ? "#fffdf2" : "transparent",
                              }}>
                                <td style={S.td}>
                                  {/* What the paper says. Always first: this is
                                      the line the user is checking against the
                                      document in their hand. */}
                                  {l.description}
                                  {/* And what dm+d calls the same product, when
                                      a barcode identified it. Both, never one
                                      replacing the other - and a mismatch
                                      between them is the fastest way to spot a
                                      bad read. */}
                                  {l.dmdName && (
                                    <div style={{ fontSize: 11.5, color: "#177245" }}
                                         title="dm+d name, resolved from the barcode">
                                      {l.dmdName}
                                      {l.dmdQtval ? ` · pack ${l.dmdQtval}` : ""}
                                    </div>
                                  )}
                                </td>
                                {/* Recorded, never matched on: there is no code
                                    on an incoming pack to match it against. */}
                                <td style={{ ...S.td, ...S.mono, fontSize: 11.5 }}>
                                  <div style={{ color: "#94a3b8" }}>
                                    {l.pip || l.pipPrinted || "no pip"}
                                    {l.pipAgrees === false && (
                                      <span style={{ color: "#b91c1c", marginLeft: 4 }}
                                            title="This PIP is already mapped to a different product than its barcode says">
                                        ✗ conflicts
                                      </span>
                                    )}
                                  </div>
                                  {/* A validated barcode that resolved in dm+d
                                      is the strongest thing on the row - it
                                      takes the line out of name matching
                                      entirely - so it is shown as such. */}
                                  <div style={{
                                    color: l.codeMatch === "gtin" ? "#177245"
                                         : l.gtin ? "#a05a00" : "#cbd5e1",
                                  }} title={
                                    l.codeMatch === "gtin"
                                      ? `Barcode resolved in dm+d${l.dmdName ? ": " + l.dmdName : ""}`
                                      : l.gtin
                                        ? "Barcode is valid but is not in dm+d - " +
                                          "this line falls back to name matching"
                                        : l.gtinPrinted
                                          ? "A barcode was printed but failed its own " +
                                            "check digit, so it was discarded"
                                          : "No barcode printed on this line"
                                  }>
                                    {l.gtin || (l.gtinPrinted ? "bad barcode" : "—")}
                                    {l.codeMatch === "gtin" && " ✓"}
                                  </div>
                                </td>
                                <td style={{ ...S.td, textAlign: "right" }}>
                                  {l.packSize ?? "—"}</td>
                                <td style={{ ...S.td, textAlign: "right",
                                             color: l.quantity === 0 ? "#b91c1c" : "inherit",
                                             fontWeight: l.quantity === 0 ? "bold" : "normal" }}>
                                  {l.quantity === 0 ? "unread" : l.quantity}</td>
                                {/* The three sources, side by side. A line
                                    where they disagree is the whole reason
                                    they are reported separately - collapsing
                                    them to the winner hides the evidence. */}
                                <td style={{ ...S.td, ...S.mono, fontSize: 11.5 }}>
                                  {l.quantitySources ? (
                                    <>
                                      {["invoice", "returns", "derived"].map((k, n) => {
                                        const src = l.quantitySources;
                                        const v = src[k];
                                        // A source can be present and still not
                                        // count. Shown struck through rather
                                        // than hidden: "the returns panel said
                                        // 1 and it was not believed" is the
                                        // useful fact, not a blank cell.
                                        const ignored =
                                          (k === "returns" && v != null &&
                                           src.returnsCounted === false) ||
                                          (k === "derived" && src.arithmeticDegenerate);
                                        const odd = v != null && !ignored &&
                                                    l.quantity > 0 && v !== l.quantity;
                                        const text = k === "derived" && src.arithmeticDegenerate
                                          ? "1?" : (v == null ? "–" : v);
                                        return (
                                          <span key={k} title={
                                            ignored
                                              ? (k === "derived"
                                                  ? "Unit price and line total were read as the " +
                                                    "same figure, so this implies 1 by definition " +
                                                    "and was not counted"
                                                  : "No pack size was reported from the returns " +
                                                    "panel, so this was not confirmed as a " +
                                                    "separate reading and was not counted")
                                              : ""
                                          } style={{
                                            color: ignored ? "#a05a00"
                                                 : v == null ? "#cbd5e1"
                                                 : odd ? "#b91c1c" : "#177245",
                                            fontWeight: odd ? "bold" : "normal",
                                            textDecoration: ignored ? "line-through" : "none",
                                          }}>
                                            {text}
                                            {n < 2 ? " / " : ""}
                                          </span>
                                        );
                                      })}
                                      {l.quantityAgreement >= 2 && (
                                        <span style={{ color: "#94a3b8", marginLeft: 4 }}>
                                          ✓{l.quantityAgreement}
                                        </span>
                                      )}
                                      {l.quantityAgreement === 1 && (
                                        <span style={{ color: "#a05a00", marginLeft: 4 }}
                                              title="Only one source counted - nothing confirms this">
                                          unconfirmed
                                        </span>
                                      )}
                                    </>
                                  ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                                </td>
                                <td style={{ ...S.td, textAlign: "right" }}>
                                  {l.unitPricePence != null
                                    ? `£${(l.unitPricePence / 100).toFixed(2)}` : "—"}</td>
                                <td style={{ ...S.td, textAlign: "right" }}>
                                  {l.lineTotalPence != null
                                    ? `£${(l.lineTotalPence / 100).toFixed(2)}` : "—"}</td>
                                <td style={{ ...S.td, textAlign: "right", ...S.mono,
                                             color: l.confidence === 0 ? "#b91c1c"
                                                  : l.confidence < 0.5 ? "#a05a00" : "#94a3b8" }}>
                                  {Number(l.confidence ?? 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {/* The arithmetic gate, which is what decides whether the
                          invoice was trusted. Worth seeing the sum it did. */}
                      {detail.storedExtraction?.normalised?.totals && (() => {
                        const t = detail.storedExtraction.normalised.totals;
                        const money = (p) => p == null ? "—" : `£${(p / 100).toFixed(2)}`;
                        const off = t.netPence != null && t.summedLinesPence != null
                          ? Math.abs(t.summedLinesPence - t.netPence) : null;
                        return (
                          <div style={{ padding: 10, borderTop: "1px solid #e2e8f0",
                                        fontSize: 12.5,
                                        background: off != null && off > 100
                                          ? "#fef2f2" : "#f8fafc" }}>
                            Lines sum to <b>{money(t.summedLinesPence)}</b> ·
                            goods total read as <b>{money(t.netPence)}</b> ·
                            VAT {money(t.vatPence)} · gross {money(t.grossPence)}
                            {off != null && (
                              <span style={{ marginLeft: 6,
                                             color: off > 100 ? "#b91c1c" : "#177245" }}>
                                {off > 100 ? `✗ out by £${(off / 100).toFixed(2)}`
                                           : "✓ reconciles"}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

                {/* stage 4 */}
                <div style={S.card}>
                  <div style={S.head}>4 · Assessment</div>
                  <div style={{ padding: 10, fontSize: 12.5 }}>
                    {detail.document.problems?.length ? (
                      <ul style={{ margin: "0 0 0 16px", color: "#a05a00" }}>
                        {detail.document.problems.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    ) : (
                      <span style={{ color: "#15803d" }}>
                        No problems recorded — the lines reconcile to the invoice total.
                      </span>
                    )}
                  </div>
                </div>

                {/* stage 5 */}
                <div style={S.card}>
                  <div style={S.head}>5 · Written to stock_invoices</div>
                  <div style={{ padding: 10, fontSize: 12.5 }}>
                    {!detail.stages.written ? (
                      <span style={{ color: "#64748b" }}>
                        Nothing written yet — a document only becomes an invoice
                        once it parses cleanly or is passed by hand.
                      </span>
                    ) : (
                      <>
                        <div><b>Invoice:</b> {detail.stages.written.invoiceNumber} ·{" "}
                          {detail.stages.written.supplierName}</div>
                        <div><b>Branch:</b> {detail.stages.written.pharmacyName || "unassigned"}</div>
                        <div><b>Status:</b> {detail.stages.written.status} ·{" "}
                          {(detail.stages.written.expectedLines || []).length} expected lines ·{" "}
                          {(detail.stages.written.lines || []).length} scanned</div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===================================================== RULES ==== */}
      {tab === "rules" && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ flex: 1, ...S.card, maxHeight: "72vh", overflowY: "auto" }}>
            <div style={S.head}>Extraction rules</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa" }}>
                <th style={S.th}>Key</th><th style={S.th}>Field</th>
                <th style={S.th}>Kind</th><th style={S.th}>Scope</th>
                <th style={S.th}>Pri</th><th style={S.th}>Pattern</th>
                <th style={S.th}></th>
              </tr></thead>
              <tbody>
                {rules.sort((a, b) => a.field.localeCompare(b.field) || a.priority - b.priority)
                      .map((r) => (
                  <tr key={r.key} style={{ borderTop: "1px solid #f1f5f9",
                                           opacity: r.enabled === false ? 0.45 : 1 }}>
                    <td style={{ ...S.td, ...S.mono }}>{r.key}</td>
                    <td style={S.td}>{r.field}</td>
                    <td style={S.td}>{r.kind}</td>
                    <td style={S.td}>
                      {r.scope === "global"
                        ? <span style={{ color: "#64748b" }}>all</span>
                        : <b>{r.scope}</b>}
                    </td>
                    <td style={S.td}>{r.priority}</td>
                    <td style={{ ...S.td, ...S.mono, maxWidth: 300, overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={r.pattern}>{r.pattern}</td>
                    <td style={S.td}>
                      <button style={{ ...S.btn(), padding: "2px 7px", fontSize: 11 }}
                        onClick={() => { setDraft({ ...r }); setTestResult(null); }}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* the editor */}
          <div style={{ flex: "0 0 400px", ...S.card, maxHeight: "72vh", overflowY: "auto" }}>
            <div style={S.head}>{draft ? "Rule" : "No rule selected"}</div>
            {!draft && (
              <div style={{ padding: 10, color: "#64748b", fontSize: 12.5 }}>
                Pick a rule to edit, or press <b>Fix</b> beside a field on the
                Pipeline tab to have one drafted from the document in front of you.
              </div>
            )}
            {draft && (
              <div style={{ padding: 10, fontSize: 12.5 }}>
                {draft.source === "ai-suggested" && (
                  <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe",
                                padding: 8, marginBottom: 8 }}>
                    <b>Drafted, not enabled.</b>
                    {draft.reasoning && <div style={{ marginTop: 3 }}>{draft.reasoning}</div>}
                    {draft.expectedValue != null && (
                      <div style={{ marginTop: 3 }}>
                        Should extract: <b>{String(draft.expectedValue)}</b>
                      </div>
                    )}
                  </div>
                )}

                {[["key", "Key"], ["pattern", "Pattern"], ["notes", "Notes"]].map(([k, label]) => (
                  <div key={k} style={{ marginBottom: 8 }}>
                    <label style={{ color: "#475569" }}>{label}</label>
                    <textarea value={draft[k] || ""} rows={k === "pattern" ? 4 : 2}
                      onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                      style={{ width: "100%", ...S.mono, padding: 6,
                               border: "1px solid #cbd5e1", boxSizing: "border-box" }} />
                  </div>
                ))}

                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <label style={{ flex: 1 }}>Field
                    <select value={draft.field} style={{ width: "100%", padding: 5 }}
                      onChange={(e) => setDraft({ ...draft, field: e.target.value })}>
                      {fields.map((f) => <option key={f}>{f}</option>)}
                    </select>
                  </label>
                  <label style={{ flex: 1 }}>Kind
                    <select value={draft.kind} style={{ width: "100%", padding: 5 }}
                      onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                      {["label", "amount", "date", "row"].map((k) => <option key={k}>{k}</option>)}
                    </select>
                  </label>
                </div>

                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <label style={{ flex: 1 }}>Scope
                    <input value={draft.scope || "global"} style={{ width: "100%", padding: 5 }}
                      onChange={(e) => setDraft({ ...draft, scope: e.target.value })} />
                  </label>
                  <label style={{ width: 80 }}>Priority
                    <input type="number" value={draft.priority ?? 50}
                      style={{ width: "100%", padding: 5 }}
                      onChange={(e) => setDraft({ ...draft, priority: +e.target.value })} />
                  </label>
                </div>

                <label style={{ display: "block", marginBottom: 10 }}>
                  <input type="checkbox" checked={draft.enabled !== false}
                    onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                  {" "}Enabled
                </label>

                <button style={S.btn()} disabled={busy === "test"}
                  onClick={() => act("test", async () => {
                    setTestResult(await post("/api/invoiceAdmin/testRule", { rule: draft }));
                  })}>
                  {busy === "test" ? "Testing…" : "Test against stored documents"}
                </button>
                <button style={S.btn("#16a34a")} disabled={busy === "save"}
                  onClick={() => act("save", async () => {
                    await post("/api/invoiceAdmin/saveRule", { rule: draft });
                    await loadRules();
                    if (selected) await openDoc({ _id: selected });
                  })}>
                  Save
                </button>

                {testResult && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                    <b>
                      {testResult.summary.documents} documents ·{" "}
                      <span style={{ color: "#15803d" }}>{testResult.summary.improved} improved</span> ·{" "}
                      <span style={{ color: testResult.summary.regressed ? "#b91c1c" : "#64748b" }}>
                        {testResult.summary.regressed} regressed
                      </span>
                    </b>
                    {testResult.summary.regressed > 0 && (
                      <div style={{ color: "#b91c1c", marginTop: 4 }}>
                        This rule takes a value away from a document that already
                        had one. Narrow the scope to one supplier, or lower its
                        priority, before saving.
                      </div>
                    )}
                    <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 6 }}>
                      {testResult.results.filter((r) => r.changed).map((r, i) => (
                        <div key={i} style={{ ...S.mono, padding: "2px 0" }}>
                          <span style={{ color: "#64748b" }}>{r.docId}</span>{" "}
                          {r.before === undefined
                            ? `rows ${r.beforeRows} → ${r.afterRows}`
                            : `${JSON.stringify(r.before)} → ${JSON.stringify(r.after)}`}
                          {r.regressed && <b style={{ color: "#b91c1c" }}> REGRESSION</b>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================================================== SETUP ==== */}
      {tab === "setup" && (
        <div>
          {/* --- suppliers --- */}
          <div style={S.card}>
            <div style={S.head}>
              Suppliers — which sender domain is which wholesaler
            </div>
            <div style={{ padding: "8px 10px", fontSize: 12.5, color: "#475569" }}>
              Until a domain is set here, a supplier is recognised only from the
              wording of the document itself. That works for the ones the parser
              already knows, but it fails quietly for anyone else — and a
              supplier that is not identified gets the generic ruleset.
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa" }}>
                <th style={S.th}>Supplier</th><th style={S.th}>Email domains</th>
                <th style={S.th}>Parser</th><th style={S.th}></th>
              </tr></thead>
              <tbody>
                {(setup?.suppliers || []).map((s) => (
                  <tr key={s._id} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={S.td}>{s.name}</td>
                    <td style={{ ...S.td, ...S.mono,
                                 color: (s.emailDomains || []).length ? "#0f172a" : "#b91c1c" }}>
                      {(s.emailDomains || []).join(", ") || "none set"}
                    </td>
                    <td style={S.td}>
                      {s.parserKey ||
                        <span style={{ color: "#a05a00" }}>fingerprint only</span>}
                    </td>
                    <td style={S.td}>
                      <button style={{ ...S.btn(), padding: "2px 7px", fontSize: 11 }}
                        onClick={() => setEditSup({
                          ...s, emailDomains: (s.emailDomains || []).join(", "),
                        })}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ padding: 10 }}>
              <button style={S.btn()}
                onClick={() => setEditSup({ name: "", emailDomains: "", parserKey: "" })}>
                + Add supplier
              </button>
            </div>

            {setup?.unmatchedSenders?.length > 0 && (
              <div style={{ padding: 10, borderTop: "1px solid #e2e8f0",
                            background: "#fffaf0" }}>
                <b style={{ fontSize: 12.5 }}>Senders with no supplier set up</b>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  These have sent invoices already — the quickest list of what
                  still needs doing.
                </div>
                {setup.unmatchedSenders.map((u, i) => (
                  <div key={i} style={{ ...S.mono, marginTop: 3 }}>
                    {u.from} <span style={{ color: "#64748b" }}>× {u.count}</span>
                    {u.domain && (
                      <button style={{ ...S.btn(), padding: "1px 6px", fontSize: 11,
                                       marginLeft: 6 }}
                        onClick={() => setEditSup({ name: "", emailDomains: u.domain,
                                                    parserKey: "" })}>
                        set up
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {editSup && (
            <div style={{ ...S.card, borderColor: "#bfdbfe" }}>
              <div style={{ ...S.head, background: "#eff6ff" }}>
                {editSup._id ? "Edit supplier" : "New supplier"}
              </div>
              <div style={{ padding: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <label style={{ flex: 1, minWidth: 180, fontSize: 12.5 }}>Name
                  <input value={editSup.name} style={{ width: "100%", padding: 5 }}
                    onChange={(e) => setEditSup({ ...editSup, name: e.target.value })} />
                </label>
                <label style={{ flex: 2, minWidth: 220, fontSize: 12.5 }}>
                  Email domains (comma separated)
                  <input value={editSup.emailDomains} style={{ width: "100%", padding: 5 }}
                    placeholder="aah.co.uk, aahpharma.com"
                    onChange={(e) => setEditSup({ ...editSup, emailDomains: e.target.value })} />
                </label>
                <label style={{ flex: 1, minWidth: 140, fontSize: 12.5 }}>Parser
                  <select value={editSup.parserKey || ""} style={{ width: "100%", padding: 5 }}
                    onChange={(e) => setEditSup({ ...editSup, parserKey: e.target.value })}>
                    <option value="">(fingerprint only)</option>
                    {(setup?.parserKeys || []).map((k) => <option key={k}>{k}</option>)}
                  </select>
                </label>
                <div style={{ alignSelf: "flex-end" }}>
                  <button style={S.btn("#16a34a")} disabled={busy === "sup"}
                    onClick={() => act("sup", async () => {
                      await post("/api/invoiceAdmin/saveSupplier", editSup);
                      setEditSup(null);
                      setSetup(await post("/api/invoiceAdmin/suppliers"));
                    })}>Save</button>
                  <button style={S.btn()} onClick={() => setEditSup(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* --- account numbers: the most reliable branch route --- */}
          <div style={S.card}>
            <div style={S.head}>
              Account numbers &mdash; which wholesaler account belongs to which branch
            </div>
            <div style={{ padding: "8px 10px", fontSize: 12.5, color: "#475569" }}>
              The strongest way to place an invoice. The account number is
              printed on every one, the wholesaler assigns it per delivery
              point, and it does not depend on anybody forwarding the email from
              the right mailbox. Worth entering up front rather than waiting for
              the system to learn it from a delivery.
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa" }}>
                <th style={S.th}>Account</th><th style={S.th}>Supplier</th>
                <th style={S.th}>Branch</th><th style={S.th}>Source</th>
                <th style={S.th}></th>
              </tr></thead>
              <tbody>
                {(accts?.accounts || []).map((a) => (
                  <tr key={a._id} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ ...S.td, ...S.mono }}>{a.accountNumber}</td>
                    <td style={S.td}>
                      {a.supplierName || <span style={{ color: "#94a3b8" }}>any</span>}
                    </td>
                    <td style={S.td}>{a.pharmacyName || a.pharmacyId}</td>
                    <td style={{ ...S.td, color: "#64748b", fontSize: 11.5 }}>
                      {a.learned ? "learned from a delivery" : "entered"}
                    </td>
                    <td style={S.td}>
                      <button style={{ ...S.btn(), padding: "2px 7px", fontSize: 11 }}
                        onClick={() => setEditAcct({ ...a })}>Edit</button>
                    </td>
                  </tr>
                ))}
                {(accts?.accounts || []).length === 0 && (
                  <tr><td colSpan={5} style={{ ...S.td, color: "#64748b" }}>None yet.</td></tr>
                )}
              </tbody>
            </table>

            <div style={{ padding: 10 }}>
              <button style={S.btn()}
                onClick={() => setEditAcct({ accountNumber: "", supplierName: "", pharmacyId: "" })}>
                + Add account number
              </button>
            </div>

            {accts?.unmatchedAccounts?.length > 0 && (
              <div style={{ padding: 10, borderTop: "1px solid #e2e8f0", background: "#fffaf0" }}>
                <b style={{ fontSize: 12.5 }}>Accounts seen on invoices but not assigned</b>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  Taken from invoices that have already arrived &mdash; the
                  shortest list of what still needs entering.
                </div>
                {accts.unmatchedAccounts.map((u, i) => (
                  <div key={i} style={{ ...S.mono, marginTop: 3 }}>
                    {u.accountNumber}
                    <span style={{ color: "#64748b" }}>
                      {" "}{u.supplierName || "unknown supplier"} &times; {u.count}
                      {u.branchHint ? ` \u00b7 document says ${u.branchHint}` : ""}
                    </span>
                    <button style={{ ...S.btn(), padding: "1px 6px", fontSize: 11, marginLeft: 6 }}
                      onClick={() => setEditAcct({ accountNumber: u.accountNumber,
                        supplierName: u.supplierName || "", pharmacyId: "" })}>assign</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {editAcct && (
            <div style={{ ...S.card, borderColor: "#bfdbfe" }}>
              <div style={{ ...S.head, background: "#eff6ff" }}>
                {editAcct._id ? "Edit account" : "New account"}
              </div>
              <div style={{ padding: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <label style={{ flex: 1, minWidth: 140, fontSize: 12.5 }}>Account number
                  <input value={editAcct.accountNumber} style={{ width: "100%", padding: 5 }}
                    onChange={(e) => setEditAcct({ ...editAcct, accountNumber: e.target.value })} />
                </label>
                <label style={{ flex: 1, minWidth: 160, fontSize: 12.5 }}>Supplier
                  <select value={editAcct.supplierName || ""} style={{ width: "100%", padding: 5 }}
                    onChange={(e) => setEditAcct({ ...editAcct, supplierName: e.target.value })}>
                    <option value="">(any supplier)</option>
                    {(accts?.suppliers || []).map((s) => <option key={s}>{s}</option>)}
                  </select>
                </label>
                <label style={{ flex: 2, minWidth: 220, fontSize: 12.5 }}>Branch
                  <select value={editAcct.pharmacyId || ""} style={{ width: "100%", padding: 5 }}
                    onChange={(e) => {
                      const b = (accts?.branches || []).find((x) => x.pharmacyId === e.target.value);
                      setEditAcct({ ...editAcct, pharmacyId: e.target.value,
                        pharmacyName: b?.pharmacyName, groupId: b?.groupId });
                    }}>
                    <option value="">Choose a branch...</option>
                    {(accts?.branches || []).map((b) => (
                      <option key={b.pharmacyId} value={b.pharmacyId}>
                        {b.organisation} &mdash; {b.pharmacyName}
                      </option>
                    ))}
                  </select>
                </label>
                <div style={{ alignSelf: "flex-end" }}>
                  <button style={S.btn("#16a34a")} disabled={busy === "acct"}
                    onClick={() => act("acct", async () => {
                      await post("/api/invoiceAdmin/saveAccount", editAcct);
                      setEditAcct(null);
                      setAccts(await post("/api/invoiceAdmin/accounts"));
                    })}>Save</button>
                  {editAcct._id && (
                    <button style={S.btn("#dc2626")} disabled={busy === "acct"}
                      onClick={() => act("acct", async () => {
                        await post("/api/invoiceAdmin/saveAccount", { _id: editAcct._id, remove: true });
                        setEditAcct(null);
                        setAccts(await post("/api/invoiceAdmin/accounts"));
                      })}>Remove</button>
                  )}
                  <button style={S.btn()} onClick={() => setEditAcct(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* --- branch addresses --- */}
          <div style={S.card}>
            <div style={S.head}>Branch addresses — which mailbox belongs to which branch</div>
            <div style={{ padding: "8px 10px", fontSize: 12.5, color: "#475569" }}>
              <b style={{ color: "#a05a00" }}>Not currently used.</b> The branch
              is decided by the account number alone: forwarded invoices arrive
              from whatever mailbox a branch happens to use, so the address they
              were sent to proves nothing about where the delivery went. These
              mappings are kept and take effect only if address routing is
              switched back on with{" "}
              <code>INVOICE_ADDRESS_ROUTING=true</code>.
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa" }}>
                <th style={S.th}>Organisation</th><th style={S.th}>Branch</th>
                <th style={S.th}>OCS</th><th style={S.th}>Invoice address</th>
                <th style={S.th}></th>
              </tr></thead>
              <tbody>
                {(addrs?.branches || []).map((b) => (
                  <tr key={b.pharmacyId} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ ...S.td, color: "#64748b" }}>{b.organisation}</td>
                    <td style={S.td}>{b.pharmacyName || b.pharmacyId}</td>
                    <td style={{ ...S.td, ...S.mono }}>{b.ocsCode || "—"}</td>
                    <td style={S.td}>
                      <input defaultValue={b.address || ""}
                        placeholder={b.ocsCode ? `${b.ocsCode.toLowerCase()}@…` : "not set"}
                        style={{ width: "100%", padding: 4, ...S.mono }}
                        onBlur={(e) => act("addr", async () => {
                          if (e.target.value === (b.address || "")) return;
                          await post("/api/invoiceAdmin/saveBranchAddress", {
                            pharmacyId: b.pharmacyId, pharmacyName: b.pharmacyName,
                            groupId: b.groupId, address: e.target.value,
                          });
                          setAddrs(await post("/api/invoiceAdmin/branchAddresses"));
                        })} />
                    </td>
                    <td style={{ ...S.td, color: b.address ? "#15803d" : "#94a3b8" }}>
                      {b.address ? "mapped" : "convention"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {addrs?.unmatchedRecipients?.length > 0 && (
              <div style={{ padding: 10, borderTop: "1px solid #e2e8f0",
                            background: "#fffaf0", fontSize: 12.5 }}>
                <b>Addresses that received invoices but matched no branch</b>
                {addrs.unmatchedRecipients.map((u, i) => (
                  <div key={i} style={S.mono}>
                    {u.to} <span style={{ color: "#64748b" }}>× {u.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================================================== INBOX ==== */}
      {tab === "ocr" && (
        <div style={S.card}>
          <div style={{ ...S.head, display: "flex",
                        justifyContent: "space-between", alignItems: "center" }}>
            <span>
              Photo extraction queue
              {ocr && Object.entries(ocr.counts || {}).map(([k, n]) => (
                <span key={k} style={{ marginLeft: 6, fontWeight: "normal",
                                       fontSize: 11.5, color: "#475569" }}>
                  {n} {k}
                </span>
              ))}
            </span>
            <span>
              <button style={S.btn()} disabled={busy === "ocr"}
                onClick={() => act("ocr", async () =>
                  setOcr(await post("/api/invoiceAdmin/ocrQueue", { limit: 100 })))}>
                Refresh
              </button>
              {ocr?.counts?.error > 0 && (
                <button style={S.btn()} disabled={busy === "req"}
                  onClick={() => act("req", async () => {
                    await post("/api/invoiceAdmin/requeueOcr", { all: true });
                    setOcr(await post("/api/invoiceAdmin/ocrQueue", { limit: 100 }));
                  })}>
                  Requeue all {ocr.counts.error} failed
                </button>
              )}
              {/* Dry run first, always. Deleting by absence is the sweep most
                  worth seeing the numbers for before it runs. */}
              <button style={{ ...S.btn(), marginRight: 0 }} disabled={busy === "sweep"}
                onClick={() => act("sweep", async () => {
                  const dry = await post("/api/invoiceAdmin/sweepOrphans",
                                         { dryRun: true });
                  const n = dry.wouldDelete.pages + dry.wouldDelete.queued;
                  if (n === 0) {
                    setError("Nothing orphaned — every page and queue row " +
                             "belongs to an invoice that still exists.");
                    return;
                  }
                  const go = window.confirm(
                    `Delete ${dry.wouldDelete.pages} page image(s) and ` +
                    `${dry.wouldDelete.queued} queue row(s) whose invoice no ` +
                    `longer exists?\n\nStage records are not touched.`);
                  if (!go) return;
                  const r = await post("/api/invoiceAdmin/sweepOrphans");
                  setError(`Swept ${r.pagesRemoved} page(s) and ` +
                           `${r.queueRemoved} queue row(s).`);
                  setOcr(await post("/api/invoiceAdmin/ocrQueue", { limit: 100 }));
                })}>
                Sweep orphans
              </button>
            </span>
          </div>

          {/* Before anything is concluded from a screen full of failures.
              "The key is missing" and "our staff cannot hold a camera straight"
              produce identical-looking queues and call for opposite responses. */}
          {ocr && !ocr.configured && (
            <div style={{ padding: 10, background: "#fef2f2", color: "#b91c1c",
                          fontSize: 12.5, borderBottom: "1px solid #fecaca" }}>
              No model is configured — set ANTHROPIC_API_KEY or OPENAI_API_KEY in
              the backend .env and restart. Every job here will fail until then,
              and none of those failures are about the photographs.
            </div>
          )}
          {ocr?.workerDisabled && (
            <div style={{ padding: 10, background: "#fff4e5", color: "#a05a00",
                          fontSize: 12.5, borderBottom: "1px solid #fed7aa" }}>
              INVOICE_OCR_DISABLE is set, so nothing is being read. Jobs will
              queue up and stay pending.
            </div>
          )}

          {!ocr && <div style={{ padding: 10 }}>Loading…</div>}
          {ocr && ocr.requests.length === 0 && (
            <div style={{ padding: 10, color: "#64748b", fontSize: 12.5 }}>
              Nothing has been photographed yet.
            </div>
          )}
          {ocr && ocr.requests.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa" }}>
                <th style={S.th}>Invoice</th>
                <th style={S.th}>Branch</th>
                <th style={S.th}>State</th>
                <th style={{ ...S.th, textAlign: "right" }}>Pages</th>
                <th style={{ ...S.th, textAlign: "right" }}>Tries</th>
                <th style={{ ...S.th, textAlign: "right" }}>Lines</th>
                <th style={S.th}>Queued</th>
                <th style={S.th}>Problem</th>
                <th style={S.th}></th>
              </tr></thead>
              <tbody>
                {ocr.requests.map((r) => {
                  const bad = r.status === "error";
                  const stuck = r.status === "processing" && r.startedAt &&
                    (Date.now() - new Date(r.startedAt).getTime()) > 5 * 60 * 1000;
                  return (
                    <tr key={r._id} style={{
                      borderTop: "1px solid #f1f5f9",
                      background: bad ? "#fef2f2" : stuck ? "#fff4e5" : "transparent",
                    }}>
                      <td style={S.td}>
                        {r.supplierName || "unknown supplier"}
                        <div style={{ fontSize: 11, color: "#64748b" }}>
                          {r.invoiceNumber || "no number"}
                        </div>
                      </td>
                      <td style={S.td}>{r.pharmacyName || "—"}</td>
                      <td style={S.td}>
                        {r.status}
                        {/* A job claimed and never released is the failure with
                            no symptom: the invoice says "reading" forever and
                            nothing ever retries it. */}
                        {stuck && (
                          <div style={{ fontSize: 11, color: "#a05a00" }}>
                            claimed over 5 min ago — probably a restart mid-run
                          </div>
                        )}
                      </td>
                      <td style={{ ...S.td, textAlign: "right" }}>{r.pageCount ?? "—"}</td>
                      <td style={{ ...S.td, textAlign: "right" }}>{r.attempts ?? "—"}</td>
                      <td style={{ ...S.td, textAlign: "right" }}>{r.lineCount ?? "—"}</td>
                      <td style={{ ...S.td, fontSize: 11.5, color: "#64748b" }}>
                        {r.requestedAt ? new Date(r.requestedAt).toLocaleString("en-GB") : "—"}
                      </td>
                      <td style={{ ...S.td, fontSize: 11.5, color: "#b91c1c" }}>
                        {r.error || ""}
                      </td>
                      <td style={S.td}>
                        {(bad || stuck) && (
                          <button style={{ ...S.btn(), marginRight: 0 }}
                            disabled={busy === `rq${r._id}`}
                            onClick={() => act(`rq${r._id}`, async () => {
                              await post("/api/invoiceAdmin/requeueOcr",
                                         { invoiceId: r.invoiceId });
                              setOcr(await post("/api/invoiceAdmin/ocrQueue", { limit: 100 }));
                            })}>
                            Requeue
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "inbox" && (
        <div style={S.card}>
          <div style={S.head}>Mailbox</div>
          {!inbox && (
            <div style={{ padding: 12, color: "#64748b" }}>
              Press <b>View inbox</b> to look without collecting anything.
            </div>
          )}
          {inbox && !inbox.available && (
            <div style={{ padding: 12, color: "#a05a00" }}>{inbox.note}</div>
          )}
          {inbox?.available && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa" }}>
                <th style={S.th}>From</th><th style={S.th}>Subject</th>
                <th style={S.th}>Received</th><th style={S.th}>Attachments</th>
                <th style={S.th}>State</th>
              </tr></thead>
              <tbody>
                {inbox.messages.map((m, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #f1f5f9",
                                       opacity: m.alreadyProcessed ? 0.5 : 1 }}>
                    <td style={S.td}>{m.from}</td>
                    <td style={S.td}>{m.subject}</td>
                    <td style={S.td}>{m.date}</td>
                    <td style={S.td}>{(m.attachments || []).join(", ") || "—"}</td>
                    <td style={S.td}>
                      {m.alreadyProcessed
                        ? <span style={{ color: "#64748b" }}>seen ({m.previousStatus})</span>
                        : <b style={{ color: "#1d4ed8" }}>new</b>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      zIndex: 300, cursor: "zoom-out" }}>
          <div style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
            {/* Unconstrained height and scrollable: an invoice is read by
                zooming into the small print, and a page politely fitted to the
                viewport is a page nobody can check a quantity against. */}
            <div style={{ maxWidth: "94vw", maxHeight: "88vh", overflow: "auto",
                          background: "#fff" }}>
              <img src={`data:${lightbox.contentType};base64,${lightbox.imageBase64}`}
                   alt={`Page ${lightbox.page}`}
                   style={{ display: "block", width: "auto", maxWidth: "none" }} />
            </div>
            <div style={{ color: "#fff", marginTop: 8, fontSize: 12.5 }}>
              Page {lightbox.page}
              {lightbox.bytes && ` · ${Math.round(lightbox.bytes / 1024)}kB`}
              <span style={{ color: "#94a3b8" }}>
                {" "}— scroll to read, click outside to close
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceProcessing;